import { Capacitor } from "@capacitor/core";

const ANDROID_PREVIEW_MAX_DIMENSION = 1024;
const ANDROID_PREVIEW_JPEG_QUALITY = 0.78;

export interface LocalImagePreviewResult {
  previewUrl: string;
  fallbackPreviewUrl?: string;
}

const readFileAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
        return;
      }
      reject(new Error("Failed to read image preview"));
    };
    reader.onerror = () =>
      reject(reader.error ?? new Error("Failed to read image preview"));
    reader.readAsDataURL(file);
  });

const toBase64DataUrl = (buffer: ArrayBuffer, mimeType: string): string => {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = "";

  for (let index = 0; index < bytes.length; index += chunkSize) {
    const chunk = bytes.subarray(index, index + chunkSize);
    binary += String.fromCodePoint(...chunk);
  }

  return `data:${mimeType};base64,${btoa(binary)}`;
};

const loadImageElement = (sourceUrl: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Failed to decode image preview"));
    image.src = sourceUrl;
  });

const createScaledPreviewDataUrl = async (file: File): Promise<string> => {
  if (typeof document === "undefined") {
    throw new TypeError("Canvas API unavailable");
  }

  const objectUrl = URL.createObjectURL(file);
  try {
    const image = await loadImageElement(objectUrl);
    const maxSide = Math.max(image.naturalWidth, image.naturalHeight);

    const scale =
      maxSide > ANDROID_PREVIEW_MAX_DIMENSION
        ? ANDROID_PREVIEW_MAX_DIMENSION / maxSide
        : 1;

    const targetWidth = Math.max(1, Math.round(image.naturalWidth * scale));
    const targetHeight = Math.max(1, Math.round(image.naturalHeight * scale));

    const canvas = document.createElement("canvas");
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Canvas context unavailable");
    }

    context.drawImage(image, 0, 0, targetWidth, targetHeight);

    // Force JPEG on Android for smaller, more reliable previews across WebViews.
    return canvas.toDataURL("image/jpeg", ANDROID_PREVIEW_JPEG_QUALITY);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

const isNativeAndroid = (): boolean =>
  Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android";

/**
 * Generates a local preview URL that is robust on Android WebView.
 *
 * - Non-Android or non-image files: blob URL
 * - Android image files: prefer data URL to avoid blob URL preview failures
 */
export const createLocalImagePreview = async (file: File): Promise<string> => {
  const result = await createLocalImagePreviewWithFallback(file);
  return result.previewUrl;
};

export const createLocalImagePreviewWithFallback = async (
  file: File,
): Promise<LocalImagePreviewResult> => {
  const isImage = file.type.startsWith("image/");
  const blobPreviewUrl = URL.createObjectURL(file);

  if (!isNativeAndroid() || !isImage) {
    return {
      previewUrl: blobPreviewUrl,
    };
  }

  try {
    const previewUrl = await createScaledPreviewDataUrl(file);
    return {
      previewUrl,
      fallbackPreviewUrl: blobPreviewUrl,
    };
  } catch {
    // Fallback to direct data URL below.
  }

  try {
    const previewUrl = await readFileAsDataUrl(file);
    return {
      previewUrl,
      fallbackPreviewUrl: blobPreviewUrl,
    };
  } catch {
    try {
      const buffer = await file.arrayBuffer();
      const previewUrl = toBase64DataUrl(buffer, file.type || "image/jpeg");
      return {
        previewUrl,
        fallbackPreviewUrl: blobPreviewUrl,
      };
    } catch {
      return {
        previewUrl: blobPreviewUrl,
      };
    }
  }
};

export const revokeObjectUrl = (previewUrl?: string | null): void => {
  if (!previewUrl) {
    return;
  }

  const normalized = previewUrl.trim();
  if (!normalized.startsWith("blob:")) {
    return;
  }

  URL.revokeObjectURL(normalized);
};
